import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { NativeScrollEvent, NativeSyntheticEvent, ScrollView, View } from 'react-native';

type CarouselHandle = {
	next: () => void;
	prev: () => void;
	scrollTo: (params: { index: number; animated?: boolean }) => void;
};

type CarouselProps<T> = {
	data: T[];
	width: number;
	height: number;
	loop?: boolean;
	pagingEnabled?: boolean;
	autoPlay?: boolean;
	scrollAnimationDuration?: number;
	snapEnabled?: boolean;
	onSnapToItem?: (index: number) => void;
	onScrollEnd?: () => void;
	renderItem: (params: { item: T; index: number }) => React.ReactNode;
};

function PlatformCarouselInner<T>(
	{
		data,
		width,
		height,
		loop = false,
		pagingEnabled = true,
		onSnapToItem,
		onScrollEnd,
		renderItem,
	}: CarouselProps<T>,
	ref: React.Ref<CarouselHandle>,
) {
	const scrollRef = useRef<ScrollView>(null);
	const [currentIndex, setCurrentIndex] = useState(0);

	const clampIndex = useCallback(
		(index: number) => {
			if (!data.length) return 0;
			if (loop) {
				if (index < 0) return data.length - 1;
				if (index >= data.length) return 0;
				return index;
			}
			return Math.max(0, Math.min(index, data.length - 1));
		},
		[data.length, loop],
	);

	const goToIndex = useCallback(
		(index: number, animated = true) => {
			if (!data.length) return;

			const safeIndex = clampIndex(index);

			scrollRef.current?.scrollTo({
				x: width * safeIndex,
				y: 0,
				animated,
			});

			setCurrentIndex(safeIndex);
			onSnapToItem?.(safeIndex);
		},
		[data.length, clampIndex, width, onSnapToItem],
	);

	useImperativeHandle(
		ref,
		() => ({
			next: () => {
				goToIndex(currentIndex + 1, true);
			},
			prev: () => {
				goToIndex(currentIndex - 1, true);
			},
			scrollTo: ({ index, animated = true }) => {
				goToIndex(index, animated);
			},
		}),
		[currentIndex, goToIndex],
	);

	const syncIndexFromOffset = useCallback(
		(event: NativeSyntheticEvent<NativeScrollEvent>) => {
			const nextIndex = clampIndex(Math.round(event.nativeEvent.contentOffset.x / width));
			setCurrentIndex(nextIndex);
			onSnapToItem?.(nextIndex);
			onScrollEnd?.();
		},
		[clampIndex, onSnapToItem, onScrollEnd, width],
	);

	return (
		<View style={{ width, height }}>
			<ScrollView
				ref={scrollRef}
				horizontal
				pagingEnabled={pagingEnabled}
				showsHorizontalScrollIndicator={false}
				onMomentumScrollEnd={syncIndexFromOffset}
				onScrollEndDrag={syncIndexFromOffset}
				scrollEventThrottle={16}
			>
				{data.map((item, index) => (
					<View key={index} style={{ width, height }}>
						{renderItem({ item, index })}
					</View>
				))}
			</ScrollView>
		</View>
	);
}

const PlatformCarousel = forwardRef(PlatformCarouselInner) as <T>(
	props: CarouselProps<T> & { ref?: React.Ref<CarouselHandle> },
) => React.ReactElement;

export default PlatformCarousel;
